import React from 'react';
import Icon, { ICONS } from '../../ui/icons';
import { useLocalization, type Language } from '../../../config/localization';

export type ViewMode = 'grid' | 'list';
export type ZoomLevel = 1 | 2 | 3 | 4 | 5;

export interface GalleryDisplaySettings {
  viewMode: ViewMode;
  zoomLevel: ZoomLevel;
}

interface GalleryDisplaySettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  settings: GalleryDisplaySettings;
  onSettingsChange: (settings: Partial<GalleryDisplaySettings>) => void;
  language: Language;
}

const GalleryDisplaySettingsPanel: React.FC<GalleryDisplaySettingsPanelProps> = ({
  isOpen,
  onClose,
  settings,
  onSettingsChange,
  language
}) => {
  const t = useLocalization(language);

  if (!isOpen) return null;

  const handleZoomChange = (newZoomLevel: ZoomLevel) => {
    onSettingsChange({ zoomLevel: newZoomLevel });
  };

  const handleViewModeChange = (newViewMode: ViewMode) => {
    onSettingsChange({ viewMode: newViewMode });
  };

  const zoomLabels = {
    1: t.gallery?.zoomSmall || 'Small',      // 1 = smallest images (most columns)
    2: t.gallery?.zoomMediumSmall || 'Medium-',
    3: t.gallery?.zoomMedium || 'Medium',
    4: t.gallery?.zoomMediumLarge || 'Medium+',
    5: t.gallery?.zoomLarge || 'Large'       // 5 = largest images (fewest columns)
  };

  return (
    <div className="w-80 bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-lg rounded-xl border-2 border-purple-500/50 shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700/40">
        <div className="flex items-center gap-2">
          <Icon name={ICONS.SLIDERS} size={20} className="text-purple-400" />
          <h3 className="text-lg font-bold text-white">
            {t.gallery?.displayOptions || 'Display Options'}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-700/50 text-gray-400 hover:text-white transition-colors"
        >
          <Icon name={ICONS.X} size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        {/* View Mode Section */}
        <div>
          <h4 className="text-sm font-medium text-gray-300 mb-3">
            {t.gallery?.viewMode || 'View Mode'}
          </h4>
          <div className="flex gap-2">
            <button
              onClick={() => handleViewModeChange('grid')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 ${
                settings.viewMode === 'grid'
                  ? 'bg-purple-600/30 border-purple-500/70 text-purple-200'
                  : 'bg-gray-800/50 border-gray-600/50 text-gray-400 hover:border-gray-500/70 hover:text-gray-200'
              }`}
            >
              <Icon name={ICONS.GALLERY} size={16} />
              <span className="text-sm font-medium">{t.gallery?.gridView || 'Grid'}</span>
            </button>
            <button
              onClick={() => handleViewModeChange('list')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 ${
                settings.viewMode === 'list'
                  ? 'bg-purple-600/30 border-purple-500/70 text-purple-200'
                  : 'bg-gray-800/50 border-gray-600/50 text-gray-400 hover:border-gray-500/70 hover:text-gray-200'
              }`}
            >
              <Icon name={ICONS.DOCUMENT} size={16} />
              <span className="text-sm font-medium">{t.gallery?.listView || 'List'}</span>
            </button>
          </div>
        </div>

        {/* Zoom Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-300">
              {t.gallery?.zoom || 'Zoom'}
            </h4>
            <span className="text-xs text-purple-400 font-medium">
              {zoomLabels[settings.zoomLevel]}
            </span>
          </div>
          
          {/* Zoom Controls */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleZoomChange(Math.max(1, settings.zoomLevel - 1) as ZoomLevel)}
              disabled={settings.zoomLevel === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-r from-purple-600/40 to-blue-600/40 hover:from-purple-500/50 hover:to-blue-500/50 border border-purple-400/40 hover:border-purple-300/60 text-white hover:text-purple-200 disabled:opacity-50 disabled:hover:from-purple-600/40 disabled:hover:to-blue-600/40 disabled:cursor-not-allowed transition-all duration-200"
            >
              <Icon name={ICONS.MINUS} size={14} />
            </button>
            
            <div className="flex-1 relative">
              <input
                type="range"
                min="1"
                max="5"
                value={settings.zoomLevel}
                onChange={(e) => handleZoomChange(parseInt(e.target.value) as ZoomLevel)}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>
            
            <button
              onClick={() => handleZoomChange(Math.min(5, settings.zoomLevel + 1) as ZoomLevel)}
              disabled={settings.zoomLevel === 5}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-r from-purple-600/40 to-blue-600/40 hover:from-purple-500/50 hover:to-blue-500/50 border border-purple-400/40 hover:border-purple-300/60 text-white hover:text-purple-200 disabled:opacity-50 disabled:hover:from-purple-600/40 disabled:hover:to-blue-600/40 disabled:cursor-not-allowed transition-all duration-200"
            >
              <Icon name={ICONS.PLUS} size={14} />
            </button>
          </div>
          
          {/* Zoom Level Indicators */}
          <div className="flex justify-between mt-2">
            {[1, 2, 3, 4, 5].map((level) => (
              <button
                key={level}
                onClick={() => handleZoomChange(level as ZoomLevel)}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                  settings.zoomLevel === level
                    ? 'bg-purple-400'
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Info Section */}
        <div className="text-xs text-gray-500 bg-gray-800/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name={ICONS.INFO} size={12} className="text-gray-400" />
            <span className="font-medium">{t.gallery?.settingsInfo || 'Display Info'}</span>
          </div>
          <p>
            {settings.viewMode === 'grid' 
              ? (t.gallery?.gridDescription || 'Grid view shows images in a responsive grid layout')
              : (t.gallery?.listDescription || 'List view shows images in a compact list format')
            }
          </p>
        </div>
      </div>

      {/* Custom slider styles */}
      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgb(147 51 234) 0%, rgb(59 130 246) 100%);
          cursor: pointer;
          border: 2px solid rgba(147, 51, 234, 0.3);
          box-shadow: 0 2px 8px rgba(147, 51, 234, 0.4);
        }
        
        .slider::-webkit-slider-thumb:hover {
          box-shadow: 0 4px 12px rgba(147, 51, 234, 0.6);
        }
        
        .slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgb(147 51 234) 0%, rgb(59 130 246) 100%);
          cursor: pointer;
          border: 2px solid rgba(147, 51, 234, 0.3);
          box-shadow: 0 2px 8px rgba(147, 51, 234, 0.4);
        }
        
        .slider::-webkit-slider-track {
          background: rgb(55 65 81);
          border-radius: 4px;
        }
        
        .slider::-moz-range-track {
          background: rgb(55 65 81);
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
};

export default GalleryDisplaySettingsPanel;